'use client';

import { c as _c } from "react/compiler-runtime";
import type { PropsChainIdMarketId } from '@morpho-org/ui-v6/types';
import type { ComponentProps, PointerEvent } from 'react';
import { ExternalLink } from '@morpho-org/ui-v6/server';
import { cn } from '@morpho-org/ui-v6/utils';
import { Separator } from '@repo/ui';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';
import type { SllCalloutEventProperties } from '@/lib/analytics/client/events';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/common/overlays';
import { SPARK_LIQUIDITY_LAYER_DOCS_URL } from '@/config/external-urls';
import { isSllMarket } from '@/config/sll';
import { trackSllCalloutOpenChange, trackSllCalloutViewed } from '@/lib/analytics/client/trackers/sll';
const SllCalloutContent = () => {
  const $ = _c(4);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <p className="text-popover-foreground text-2xs">This market is automated by the Spark Liquidity Layer. More liquidity will be added as you borrow to maintain the borrow rate. Capacity depends on Spark's available liquidity.{" "}<ExternalLink className="text-info-foreground" href={SPARK_LIQUIDITY_LAYER_DOCS_URL} underlined={true}>Learn more</ExternalLink></p>;
    t1 = <Separator />;
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span className="text-muted-foreground text-4xs">Automation powered by</span>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="gap-sm p-md flex flex-col items-start">{t0}{t1}<div className="gap-2xs flex flex-col items-start">{t2}<ExternalLink aria-label="Spark Liquidity Layer documentation" className="block transition-opacity hover:opacity-80" href={SPARK_LIQUIDITY_LAYER_DOCS_URL}><Image alt="" className="h-4 w-[146px] dark:hidden" height={16} src="/assets/sll-logo-wordmark.png" width={146} /><Image alt="" className="hidden h-4 w-[146px] dark:block" height={16} src="/assets/sll-logo-wordmark-dark.png" width={146} /></ExternalLink></div></div>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
};

/**
 * Spark's own brand colours, not the Morpho accent: recolouring the logomark
 * would cost the recognition that makes it a trust signal.
 */
const SllTrigger = t0 => {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("focus-visible:ring-ring/50 flex size-6 shrink-0 cursor-help items-center justify-center rounded-full transition-opacity hover:opacity-80 focus-visible:ring-[3px] focus-visible:outline-none", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Image alt="" className="size-full" height={24} src="/assets/sll-logo.svg" width={24} />;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== props || $[7] !== t1) {
    t3 = <button {...props} aria-label="Spark Liquidity Layer automated market" className={t1} type="button">{t2}</button>;
    $[6] = props;
    $[7] = t1;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
};

/**
 * Popover, not HoverCard: Radix forces `tabindex="-1"` onto everything inside
 * hover-card content and closes it on trigger blur, which would leave the links
 * mouse-only. Hover-to-open is layered back on for pointer devices.
 */
const SllCallout = t0 => {
  const $ = _c(32);
  const {
    chainId,
    marketId,
    source,
    className
  } = t0;
  const [isOpen, setIsOpen] = useState(false);
  const openRef = useRef(false);
  const openedByHover = useRef(false);
  const hoverTimer = useRef(undefined);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => () => clearTimeout(hoverTimer.current);
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  const viewed = useRef(false);
  let t3;
  let t4;
  if ($[2] !== chainId || $[3] !== marketId || $[4] !== source) {
    t3 = () => {
      if (viewed.current) {
        return;
      }
      viewed.current = true;
      trackSllCalloutViewed({
        source,
        chain_id: chainId,
        market_id: marketId
      });
    };
    t4 = [source, chainId, marketId];
    $[2] = chainId;
    $[3] = marketId;
    $[4] = source;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  let t5;
  if ($[7] !== chainId || $[8] !== marketId || $[9] !== source) {
    t5 = (next, t6) => {
      const byHover = t6 === undefined ? false : t6;
      if (openRef.current === next) {
        return;
      }
      openRef.current = next;
      if (next) {
        openedByHover.current = byHover;
      }
      setIsOpen(next);
      trackSllCalloutOpenChange(next, {
        source,
        chain_id: chainId,
        market_id: marketId
      });
    };
    $[7] = chainId;
    $[8] = marketId;
    $[9] = source;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const setOpen = t5;
  let t6;
  if ($[11] !== setOpen) {
    t6 = next_0 => {
      clearTimeout(hoverTimer.current);
      hoverTimer.current = setTimeout(() => setOpen(next_0, true), next_0 ? 200 : 100);
    };
    $[11] = setOpen;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const scheduleHover = t6;
  let t7;
  if ($[13] !== scheduleHover) {
    t7 = {
      onPointerEnter: event => {
        if (event.pointerType === "mouse") {
          scheduleHover(true);
        }
      },
      onPointerLeave: event_0 => {
        if (event_0.pointerType === "mouse") {
          scheduleHover(false);
        }
      }
    };
    $[13] = scheduleHover;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const hoverProps = t7;
  let t8;
  if ($[15] !== setOpen) {
    t8 = next_1 => {
      clearTimeout(hoverTimer.current);
      setOpen(next_1);
    };
    $[15] = setOpen;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== className) {
    t9 = <SllTrigger className={className} />;
    $[17] = className;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== hoverProps || $[20] !== t9) {
    t10 = <PopoverTrigger asChild={true} {...hoverProps}>{t9}</PopoverTrigger>;
    $[19] = hoverProps;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  let t12;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = event_1 => {
      if (openedByHover.current) {
        event_1.preventDefault();
      }
    };
    t12 = event_2 => {
      if (openedByHover.current) {
        event_2.preventDefault();
      }
    };
    $[22] = t11;
    $[23] = t12;
  } else {
    t11 = $[22];
    t12 = $[23];
  }
  let t13;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <SllCalloutContent />;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== hoverProps) {
    t14 = <PopoverContent {...hoverProps} onCloseAutoFocus={t11} onOpenAutoFocus={t12} aria-label="Spark Liquidity Layer" className="w-80 p-0" sideOffset={8}>{t13}</PopoverContent>;
    $[25] = hoverProps;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== isOpen || $[28] !== t10 || $[29] !== t14 || $[30] !== t8) {
    t15 = <Popover onOpenChange={t8} open={isOpen}>{t10}{t14}</Popover>;
    $[27] = isOpen;
    $[28] = t10;
    $[29] = t14;
    $[30] = t8;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  return t15;
};
interface SllBadgeProps extends PropsChainIdMarketId {
  source: SllCalloutEventProperties['source'];
  className?: string;
}

/**
 * Guard outside `SllCallout`, so uncovered markets mount no state or timers.
 * Keyed by market: index-keyed table rows would otherwise hand one market's
 * impression and open state to another.
 */
export const SllBadge = t0 => {
  const $ = _c(5);
  const {
    chainId,
    marketId,
    source,
    className
  } = t0;
  let t1;
  if ($[0] !== chainId || $[1] !== className || $[2] !== marketId || $[3] !== source) {
    t1 = isSllMarket(chainId, marketId) ? <SllCallout key={`${chainId}:${marketId}`} chainId={chainId} className={className} marketId={marketId} source={source} /> : null;
    $[0] = chainId;
    $[1] = className;
    $[2] = marketId;
    $[3] = source;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
};